import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BrandLogo } from './ui/BrandLogo';
import { ProviderNav } from './ui/ProviderNav';
import { ProviderFooter } from './ui/ProviderFooter';
import { CookieConsent } from './ui/CookieConsent';
import { SupportContact } from './ui/SupportContact';

const ScrollToTopButton = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 320);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <button
      type="button"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label="Scroll to top"
      className={`provider-scroll-top${visible ? ' provider-scroll-top--visible' : ''}`}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
        <polyline points="6 15 12 9 18 15" />
      </svg>
    </button>
  );
};

const DEFAULT_SPLIT_IMAGE = '/images/hero-restaurant.png';

/**
 * Photographic split-screen used across the onboarding / auth flow.
 * Left: full-bleed editorial photograph with brand + a line of copy.
 * Right: scrollable form column.
 */
const AuthSplitLayout = ({
  children,
  image = DEFAULT_SPLIT_IMAGE,
  eyebrow = 'ReTechSys Provider Portal',
  quote = 'Clarity for every cup. Effortless billing for every venue.',
  attribution,
}) => (
  <div className="provider-shell provider-shell--split">
    <aside
      className="auth-visual"
      style={{ backgroundImage: `url(${image})` }}
    >
      <div className="auth-visual__top">
        <BrandLogo smartHome className="provider-brand--ondark" />
      </div>
      <div className="auth-visual__bottom">
        {eyebrow && <p className="auth-visual__eyebrow">{eyebrow}</p>}
        {quote && <p className="auth-visual__quote">{quote}</p>}
        {attribution && <p className="auth-visual__attr">{attribution}</p>}
      </div>
    </aside>

    <div className="auth-form-col">
      <header className="auth-form-col__bar">
        <BrandLogo smartHome />
      </header>
      <div className="auth-form-col__inner">{children}</div>
      <footer className="auth-form-col__foot">
        <Link to="/provider/terms">Terms</Link>
        <Link to="/provider/privacy">Privacy</Link>
        <Link to="/provider/cookies">Cookies</Link>
        <SupportContact variant="link" triggerLabel="Support" triggerClassName="provider-footer-support" />
      </footer>
    </div>

    <CookieConsent />
  </div>
);

/**
 * @param {'auth' | 'app' | 'split'} variant
 *   auth = centered card; app = full-width with nav; split = photographic onboarding
 * @param {boolean} wide - use wide main container
 * @param {function} onSignOut - enables app nav sign-out
 * @param {boolean} bare - no max-width wrapper (legal pages manage their own)
 * @param {string} splitImage - hero photo for the split variant
 * @param {string} splitEyebrow - small overline over the split quote
 * @param {string} splitQuote - editorial line on the split visual
 * @param {string} splitAttribution - attribution under the quote
 */
const ProviderPageLayout = ({
  children,
  variant = 'auth',
  wide = false,
  onSignOut,
  showHeader = true,
  showFooter = true,
  bare = false,
  splitImage,
  splitEyebrow,
  splitQuote,
  splitAttribution,
}) => {
  if (variant === 'split') {
    return (
      <AuthSplitLayout
        image={splitImage}
        eyebrow={splitEyebrow}
        quote={splitQuote}
        attribution={splitAttribution}
      >
        {children}
      </AuthSplitLayout>
    );
  }

  const isApp = variant === 'app';

  return (
    <div className={`provider-shell${isApp ? ' provider-shell--app' : ' provider-shell--auth'}`}>
      {showHeader &&
        (isApp && onSignOut ? (
          <ProviderNav onSignOut={onSignOut} />
        ) : (
          <header className="provider-header provider-header--minimal">
            <BrandLogo smartHome />
          </header>
        ))}

      <main
        className={[
          'provider-main',
          bare ? '' : isApp ? 'provider-main--wide' : 'provider-main--auth',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {children}
      </main>

      {showFooter && <ProviderFooter />}
      <ScrollToTopButton />
      <CookieConsent />
    </div>
  );
};

export default ProviderPageLayout;
